import { useMutation, useQueryClient } from 'react-query';
import { StatusResponse } from '../../../../../../../types/API';
import { getUseAdvisoryReportQKey } from '../../../../../../advisory-report/src/api/getAdvisoryReport';
import { AdvisoryReport } from '../../../../../../advisory-report/src/types/AdvisoryReport';
import { wgApi } from '../../../../../../app/src/features/api/wgApi';
import {
  getUseInsurancePolicyPackagesQKey,
  InsurencePolicyPackagesPartialResponse,
} from './getInsurancePolicyPackagesByParty';

interface Params {
  partyId: string;
  packageId: string;
  reportId?: string;
}

const deleteInsurancePolicyPackage = async ({ partyId, packageId }: Params) => {
  const { data, isError } = await wgApi.delete<StatusResponse>(
    `/v1/api/parties/${partyId}/policy-packages/${packageId}`,
  );
  if (isError) throw new Error();
  return data;
};

export const useRemoveInsurancePolicyPackage = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteInsurancePolicyPackage, {
    onMutate: async ({ partyId, packageId, reportId }) => {
      let previousReport;
      // Update report data
      if (reportId) {
        const reportQKey = getUseAdvisoryReportQKey(reportId);
        await queryClient.cancelQueries(reportQKey);

        previousReport = queryClient.getQueryData<AdvisoryReport>(reportQKey);

        if (previousReport) {
          const newPackages = previousReport.policy_packages?.filter(
            ({ id }) => id !== packageId,
          );
          queryClient.setQueryData(reportQKey, {
            ...previousReport,
            policy_packages: newPackages,
          });
        }
      }

      const policyPackagesQkey = getUseInsurancePolicyPackagesQKey({
        partyId,
      });

      await queryClient.cancelQueries(policyPackagesQkey);

      const previousPackages =
        queryClient.getQueryData<InsurencePolicyPackagesPartialResponse>(
          policyPackagesQkey,
        );

      queryClient.setQueryData<InsurencePolicyPackagesPartialResponse>(
        policyPackagesQkey,
        (prev) => {
          // Ensure prev is defined
          const previousData = prev || { items: [] };

          const filteredItems = previousData.items.filter(
            ({ id }) => id !== packageId,
          );

          return {
            ...previousData,
            items: filteredItems,
          };
        },
      );

      return { previousPackages, previousReport };
    },
    onError: (_, { partyId, reportId }, ctx) => {
      if (reportId && ctx?.previousReport) {
        const reportQKey = getUseAdvisoryReportQKey(reportId);
        queryClient.setQueryData(reportQKey, ctx?.previousReport);
      }
      const policyPackagesQkey = getUseInsurancePolicyPackagesQKey({
        partyId,
      });
      queryClient.setQueryData(policyPackagesQkey, ctx?.previousPackages);
    },
    onSuccess: (_, { partyId }) => {
      queryClient.invalidateQueries(
        getUseInsurancePolicyPackagesQKey({ partyId }),
      );
    },
  });
};
